import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, LinearProgress } from '@mui/material';
import moment from 'moment/moment';
import { toast } from 'react-toastify';
// COMPONENTS
import Header from './components/Header';
import InventoryCards from './components/InventoryCards';
import ImportModal from './components/ImportModal';
import AllocationDetails from './components/AllocationDetails';
import NotFoundTemplate from '../../components/AppLayout/NotFoundTemplate';
// Context
import { DashboardProvider } from '../Dashboard/useDashboard';
import useAllocationContext from './context/useAllocationContext';
// API
import {
  getProductConfirmedInventory,
  getSamplingPlanView,
  postProductAllocationTemplateExport,
  postFinalizeAllocation,
} from '../../api/productSamplingWorkflowApi';
// HELPERS

export default function ProductSamplingWorkflowAllocation() {
  const [searchParams] = useSearchParams();
  const plan_id = searchParams.get('plan_id');

  const defaultFilterForm = {
    month_year: moment().format('YYYY-MM'),
    status: 0,
    category_id: 0,
  };

  const TIMEOUT_TIME = 3000;
  const NOT_ALLOWABLE_ALLOCATION = [1, 2, 3];
  const [loading, setLoading] = useState(true);
  const [filterForm, setFilterForm] = useState(defaultFilterForm);
  const [plan, setPlan] = useState({});
  const [inventory, setInventory] = useState({
    loading: true,
    total_confirmed: 0,
    total_allocated: 0,
    total_remaining: 0,
    items: [],
  });
  const [importModal, setImportModal] = useState({ toggle: false });
  const [planNotFound, setPlanNotFound] = useState(false);

  const loadSamplingPlan = async () => {
    try {
      const response = await getSamplingPlanView({ id: plan_id });
      const { code, data } = response.data;

      if (code !== '200' || !data || NOT_ALLOWABLE_ALLOCATION.includes(data.status_id)) {
        setPlanNotFound(true);
        return;
      }
      setPlan(data);
      setPlanNotFound(false);
    } catch (error) {
      setPlanNotFound(true);
    }
  };
  const loadConfirmedInventoryList = async () => {
    setInventory((prev) => ({
      ...prev,
      loading: true,
    }));
    const request = {
      plan_id: plan_id,
      month: null,
      year: null,
    };

    const response = await getProductConfirmedInventory(request);
    const { code, data, total_confirmed, total_allocated, total_remaining } = response?.data;

    if (code === '200') {
      setInventory((prev) => ({
        ...prev,
        loading: false,
        total_confirmed: total_confirmed,
        total_allocated: total_allocated,
        total_remaining: total_remaining,
        items: data.filter((row) => row.product_code !== null),
      }));
    }
  };

  const handleImportModal = () => {
    setImportModal({ ...importModal, toggle: true });
  };

  const handleExportTemplate = async () => {
    try {
      toast.loading('Processing request please kindly wait...');
      const request = {
        plan_id: plan.plan_id,
        month: Number(moment(plan.effectivity_month, 'MMMM').format('M')),
        year: plan.effectivity_year,
      };

      const response = await postProductAllocationTemplateExport(request);
      const { code, result, file_name } = response.data.response;

      if (code === '200') {
        toast.dismiss();
        window.location.href = result;
        toast.success(`file has been successfully exported: ${file_name}`);
      }
    } catch (error) {
      toast.dismiss();
      toast.error(error.message);
    }
  };

  const handleFinalizeAllocation = async () => {
    try {
      toast.loading('Processing request please kindly wait...');
      const request = {
        id: plan.plan_id,
      };

      const response = await postFinalizeAllocation(request);
      const { code, message } = response.data;

      toast.dismiss();
      if (code === '200') {
        toast.success(message);
        setTimeout(() => window.location.reload(), TIMEOUT_TIME);
      }
    } catch (error) {
      toast.dismiss();
      toast.error(error.message);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      try {
        setLoading(true);

        await Promise.all([loadSamplingPlan(), loadConfirmedInventoryList()]);

        setLoading(false);
      } catch (error) {
        setLoading(false);
        toast.dismiss();
        toast.error(error.message);
      }
    };

    initialize();
  }, []);

  return (
    <DashboardProvider>
      <useAllocationContext.Provider
        value={useMemo(
          () => ({
            plan,
            inventory,
            filterForm,
            importModal,
            setImportModal,
            setInventory,
            setFilterForm,
            handleImportModal,
            loadConfirmedInventoryList,
            handleExportTemplate,
            handleFinalizeAllocation,
          }),
          [plan, inventory, filterForm, importModal],
        )}
      >
        {loading ? (
          <Box sx={{ width: '100%' }}>
            <LinearProgress />
          </Box>
        ) : planNotFound ? (
          <NotFoundTemplate
            active={true}
            body='Plan is not confirmed yet. Confirm the plan to proceed with allocation'
          />
        ) : (
          <Box sx={{ bgcolor: '#f5f5f5' }} p={3}>
            <Header />
            <InventoryCards />
            <AllocationDetails />
            {importModal.toggle && <ImportModal />}
          </Box>
        )}
      </useAllocationContext.Provider>
    </DashboardProvider>
  );
}
